import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';

const POLL_INTERVAL_MS = 2500;
const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled']);

/**
 * Track one document-generation request (ai_requests row) until it finishes, then load the
 * generated document so the page can show per-field status.
 *
 * @param {string|null} requestId - ai_requests id, or null when nothing is running.
 * @returns {{request: object|null, document: object|null, error: string}} The latest request row
 *   (status, progress, progress_message, error_message, result_data), the generated document
 *   (a generated_documents row: id, name, content, trust_metadata, project_id, template_id,
 *   template_name, requirements_count, created_at, updated_at) once completed, and a load error.
 */
export default function useGenerationRequest(requestId) {
  const [request, setRequest] = useState(null);
  const [document, setDocument] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setRequest(null);
    setDocument(null);
    setError('');
    if (!requestId) return undefined;

    let cancelled = false;
    let timer = null;

    const loadDocument = async (documentId) => {
      const { data, error: docError } = await supabase
        .from('generated_documents')
        .select('id, name, content, trust_metadata, project_id, template_id, template_name, requirements_count, created_at, updated_at')
        .eq('id', documentId)
        .maybeSingle();
      if (cancelled) return;
      if (docError || !data) {
        setError('The document was generated, but it could not be loaded here. Open it from the project.');
        return;
      }
      setDocument(data);
    };

    const poll = async () => {
      const { data, error: requestError } = await supabase
        .from('ai_requests')
        .select('id, status, progress, progress_message, error_message, result_data')
        .eq('id', requestId)
        .maybeSingle();
      if (cancelled) return;
      if (requestError) {
        // Transient network/RLS hiccup: keep polling, the notification panel tracks it too.
        console.warn('Could not read generation status:', requestError.message);
      } else if (data) {
        setRequest(data);
        if (TERMINAL_STATUSES.has(data.status)) {
          if (data.status === 'completed' && data.result_data?.documentId) {
            await loadDocument(data.result_data.documentId);
          }
          return;
        }
      }
      timer = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [requestId]);

  return { request, document, error };
}
